import Vue from 'vue';
import { Component, Prop, Provide, Watch } from 'vue-property-decorator';
import { Options, SeriesOptionsType } from 'highcharts';
import { get, merge } from 'lodash';

export type TColorRange = [number, number, number];

export type TChart = {
  // CHart options
  options: Options;

  // Getter
  readonly series: SeriesOptionsType[];

  setOptions(options: Options): void;
  setSeries(series: SeriesOptionsType[]): void;
  color(value: number, range: TColorRange): string;

  created(): void;
};

export type TErrorRange = {
  a: number;
  b: number;
};

export type TMode = 'absolute' | 'relative' | 'percentage';
export type TAcquisitionMode = 'standard' | 'running';

export type TChartMixin = Omit<TChart, 'series' | 'created'>;

export type SeriesOptionsTypeExtended = SeriesOptionsType & {
  openRow?: boolean;
  closeRow?: boolean;
};

@Component
export default class MixinChart extends Vue implements TChartMixin {
  @Prop({ default: 'absolute' }) protected mode!: TMode;
  @Prop({ default: false }) protected comparison!: boolean;
  @Prop({ default: null }) protected virtualName!: string;
  @Prop({ default: null }) protected height!: number;
  @Prop(Boolean) protected simple!: boolean | number;
  @Prop(Boolean) protected hideTitle!: boolean;
  @Prop(Boolean) protected hideLegends!: boolean;

  // Chart name for exportation
  @Provide() name: string | null = null;

  // Chart options
  @Provide() options: Options = {};

  setOptions(options: Options) {
    this.options = this.simple
      ? merge(options, {
          chart: {
            animation: false,
            width:
              typeof this.simple === 'number'
                ? this.simple
                : (document.getElementById('export-dpi-sensor')!.offsetWidth / 25.4) * (210 - 14 * 2),
          },
          legend: {
            enabled: !this.hideLegends,
          },
          plotOptions: {
            series: {
              enableMouseTracking: false,
              animation: false,
            },
          },
          exporting: { enabled: false },
        } as Options)
      : options;
  }

  @Watch('series', { deep: true })
  setSeries(series: SeriesOptionsType[]) {
    this.options.series = series;
  }

  color(value: number, range: TColorRange): string {
    switch (true) {
      case value >= range[0] && value <= range[1]:
        return this.$vuetify.theme.themes.light.error as string;
      case value > range[1] && value <= range[2]:
        return this.$vuetify.theme.themes.light.warning as string;
      default:
        return this.$vuetify.theme.themes.light.success as string;
    }
  }

  sendToExportation() {
    if (!this.name && !this.virtualName) throw new Error('Missing virtual name');

    if (get(this.$options, '_parentListeners.exportation')) {
      // Check if charts have ended his rendering
      if (!arguments[0].renderer) {
        console.log(`[EXPORT] ⌛ Delay "${this.virtualName || this.name}" exportation ...`);

        setTimeout(() => {
          // @ts-ignore
          this.sendToExportation.call(this, ...arguments);
        }, 1000);
      } else {
        console.log(`[EXPORT] 📡 Send "${this.virtualName || this.name}" to exportation`);
        this.$emit('exportation', [this.virtualName || this.name, ...arguments]);
      }
    }

    return arguments;
  }
}
